<template>
    <div ref="oddsComponent" class="oddBox show-option">
        <slot name="oddsLabel" />

        <div
            v-if="oddsInfo"
            v-bind:class="[
                'odd',
                'JARVIS_MatchCard_Btn_Odds',
                {
                    odd_rise: oddRise,
                    odd_drop: oddDrop,
                    odd_selected: isOddsSelected,
                    odd_locked: isOddsSuspended,
                },
            ]"
            v-bind:data-match="gaDataMatch"
            v-bind:market-type-data-list="marketOrder"
            v-bind:match-card-data-list="matchInfo.matchOrder"
            v-on:click="onClickOdds"
        >
            <div v-if="!isOddsSuspended" class="odd-option">
                <span>{{ optionNameDisplay }}</span>
            </div>

            <div v-if="!isOddsSuspended" class="odd-wrapper">
                <div class="odd-align-left-wrapper">
                    <div class="odd-point">
                        {{ toFixedDecimalPoint(point, 2) }}
                    </div>
                    <div
                        class="odd-value"
                        v-bind:class="{ _negative: isOddsNegative, rtl: isReverseDisplayOddsEnabled }"
                    >
                        {{ strReverse(formatMinDecimalPlace(priceOption.price, minimumDecimalPlace), isReverseDisplayOddsEnabled) }}
                    </div>
                </div>
                <div class="odd-align-right-wrapper" />
            </div>

            <div v-if="isOddsSuspended" class="odd-locked">
                <SvgIcon iconName="systemIcon_Locked" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    // This Odds component will show the optionName from odds.
    // There are some differnt in the dom structure between original one
    // and this to achieve the effect we want
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs } from 'vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds, IPriceOption } from '@/interface/IOdds';
    import { DisplaySection } from '@/interface/enum';
    import useOddsDisplay from '@/libraries/composables/useOddsDisplay';
    import PriceOptionLib from '@/libraries/priceOption';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'OptionOdds',
        components: {
            SvgIcon,
        },
        props: {
            matchInfo: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            oddsInfo: {
                type: Object as PropType<IOdds>,
                required: true,
            },
            priceOption: {
                type: Object as PropType<IPriceOption>,
                required: true,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
            marketOrder: {
                type: Number,
                default: -1,
            },
        },
        setup(props) {
            const optionNameDisplay = computed(() => {
                if (PriceOptionLib.betOptionUseTemplate(props.oddsInfo.marketType, props.oddsInfo.marketGroup)) {
                    return props.priceOption.optionName;
                }
                return PriceOptionLib.getOptionName(props.priceOption.optionName, props.matchInfo, props.oddsInfo.marketType);
            });

            const { matchInfo, oddsInfo, priceOption, displaySection } = toRefs(props);
            const oddsComponent = ref<HTMLElement | null>(null);

            const {
                oddRise,
                oddDrop,
                isOddsNegative,
                isOddsSuspended,
                isOddsSelected,
                point,
                minimumDecimalPlace,
                gaDataMatch,
                toFixedDecimalPoint,
                onClickOdds,
                formatMinDecimalPlace,
                strReverse,
                isReverseDisplayOddsEnabled,
            } = useOddsDisplay({
                matchInfo,
                oddsInfo,
                priceOption,
                displaySection,
                oddsComponentRef: oddsComponent,
            });

            return {
                optionNameDisplay,
                toFixedDecimalPoint,
                minimumDecimalPlace,
                point,
                oddRise,
                oddDrop,
                isOddsNegative,
                isOddsSuspended,
                isOddsSelected,
                strReverse,
                formatMinDecimalPlace,
                isReverseDisplayOddsEnabled,
                onClickOdds,
                gaDataMatch,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/odd";
</style>
